/**
 * PayrollContext - 급여 관리 통합 Context
 *
 * Phase 2 Step 5: Context 도입
 *
 * @description
 * - 성능 최적화를 위한 Context 분리 (UI, Loading, Settings)
 * - 각 Context별로 독립적인 구독 가능
 * - 불필요한 리렌더링 방지
 */

import React, { createContext, useContext, useMemo } from 'react';
import {
  usePayrollUIState,
  usePayrollLoading,
  usePayrollSettings,
  type UsePayrollUIStateReturn,
  type UsePayrollLoadingReturn,
  type UsePayrollSettingsReturn,
} from '../hooks/payroll/index.ts';
import { AppError, ErrorCodes } from '../utils/errorHandler.ts';

// ============================================
// Context Types
// ============================================

export interface PayrollUIContextType extends UsePayrollUIStateReturn {}
export interface PayrollLoadingContextType extends UsePayrollLoadingReturn {}
export interface PayrollSettingsContextType extends UsePayrollSettingsReturn {}

// 통합 Context Type (모든 상태를 한번에 접근할 때 사용)
export interface PayrollContextType {
  ui: PayrollUIContextType;
  loading: PayrollLoadingContextType;
  settings: PayrollSettingsContextType;
}

// ============================================
// Context Definitions
// ============================================

// 개별 Context (성능 최적화용 - 필요한 것만 구독)
export const PayrollUIContext = createContext<PayrollUIContextType | null>(null);
export const PayrollLoadingContext = createContext<PayrollLoadingContextType | null>(null);
export const PayrollSettingsContext = createContext<PayrollSettingsContextType | null>(null);

// 통합 Context (편의성용 - 모든 상태 한번에 접근)
export const PayrollContext = createContext<PayrollContextType | null>(null);

// ============================================
// Provider Component
// ============================================

interface PayrollProviderProps {
  children: React.ReactNode;
}

/**
 * PayrollProvider - 급여 관리 상태 제공자
 *
 * 사용법:
 * <PayrollProvider>
 *   <PayrollManagement />
 * </PayrollProvider>
 */
export function PayrollProvider({ children }: PayrollProviderProps) {
  // Hook 호출
  const uiState = usePayrollUIState();
  const loadingState = usePayrollLoading();
  const settingsState = usePayrollSettings();

  // 통합 Context 값 메모이제이션
  const combinedValue = useMemo<PayrollContextType>(() => ({
    ui: uiState,
    loading: loadingState,
    settings: settingsState,
  }), [uiState, loadingState, settingsState]);

  return (
    <PayrollContext.Provider value={combinedValue}>
      <PayrollUIContext.Provider value={uiState}>
        <PayrollLoadingContext.Provider value={loadingState}>
          <PayrollSettingsContext.Provider value={settingsState}>
            {children}
          </PayrollSettingsContext.Provider>
        </PayrollLoadingContext.Provider>
      </PayrollUIContext.Provider>
    </PayrollContext.Provider>
  );
}

// ============================================
// Custom Hooks for Consumers
// ============================================

/**
 * UI 상태만 필요할 때 사용
 * - 모달, 비교 모드, IME 입력 등
 */
export function usePayrollUI(): PayrollUIContextType {
  const context = useContext(PayrollUIContext);
  if (!context) {
    throw new AppError(
      'usePayrollUI must be used within a PayrollProvider',
      ErrorCodes.VALIDATION_ERROR,
      { hook: 'usePayrollUI' }
    );
  }
  return context;
}

/**
 * 로딩 상태만 필요할 때 사용
 * - 데이터 로딩, 저장 진행, 실행 상태 등
 */
export function usePayrollLoadingContext(): PayrollLoadingContextType {
  const context = useContext(PayrollLoadingContext);
  if (!context) {
    throw new AppError(
      'usePayrollLoadingContext must be used within a PayrollProvider',
      ErrorCodes.VALIDATION_ERROR,
      { hook: 'usePayrollLoadingContext' }
    );
  }
  return context;
}

/**
 * 설정 상태만 필요할 때 사용
 * - 공제율, 연장근무 배수, 조정 설정 등
 */
export function usePayrollSettingsContext(): PayrollSettingsContextType {
  const context = useContext(PayrollSettingsContext);
  if (!context) {
    throw new AppError(
      'usePayrollSettingsContext must be used within a PayrollProvider',
      ErrorCodes.VALIDATION_ERROR,
      { hook: 'usePayrollSettingsContext' }
    );
  }
  return context;
}

/**
 * 모든 Payroll 상태가 필요할 때 사용
 * - payroll.ui, payroll.loading, payroll.settings로 접근
 */
export function usePayrollContext(): PayrollContextType {
  const context = useContext(PayrollContext);
  if (!context) {
    throw new AppError(
      'usePayrollContext must be used within a PayrollProvider',
      ErrorCodes.VALIDATION_ERROR,
      { hook: 'usePayrollContext' }
    );
  }
  return context;
}

// ============================================
// Selector Hooks (선택적 구독용)
// ============================================

/**
 * UI 상태 중 특정 값만 선택적으로 구독
 * 예: const isModalOpen = usePayrollUISelector(state => state.showPayrollWarningModal);
 */
export function usePayrollUISelector<T>(
  selector: (state: PayrollUIContextType) => T
): T {
  const context = usePayrollUI();
  return selector(context);
}

/**
 * 로딩 상태 중 특정 값만 선택적으로 구독
 * 예: const isSaving = usePayrollLoadingSelector(state => state.isSaving);
 */
export function usePayrollLoadingSelector<T>(
  selector: (state: PayrollLoadingContextType) => T
): T {
  const context = usePayrollLoadingContext();
  return selector(context);
}

/**
 * 설정 상태 중 특정 값만 선택적으로 구독
 * 예: const rate = usePayrollSettingsSelector(state => state.underperformanceDeductionRate);
 */
export function usePayrollSettingsSelector<T>(
  selector: (state: PayrollSettingsContextType) => T
): T {
  const context = usePayrollSettingsContext();
  return selector(context);
}

// ============================================
// Display Names (DevTools용)
// ============================================

PayrollUIContext.displayName = 'PayrollUIContext';
PayrollLoadingContext.displayName = 'PayrollLoadingContext';
PayrollSettingsContext.displayName = 'PayrollSettingsContext';
PayrollContext.displayName = 'PayrollContext';

// ============================================
// Exports
// ============================================

export default PayrollProvider;
